import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Label } from '../components/ui/label';
import { Calendar as CalendarIcon, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EditEvent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [date, setDate] = useState(undefined);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    start_time: '',
    end_time: '',
    location: '',
    address: '',
    capacity: '',
    registration_link: '',
    image_url: '',
  });

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    fetchEvent();
    fetchCategories();
  }, [user, navigate, id, authLoading]);

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API}/events/${id}`);
      const event = response.data;
      
      if (event.organizer_id !== user.id) {
        toast.error('Not authorized to edit this event');
        navigate('/events');
        return;
      }

      setFormData({
        title: event.title,
        description: event.description,
        category: event.category,
        start_time: event.start_time,
        end_time: event.end_time,
        location: event.location,
        address: event.address,
        capacity: event.capacity || '',
        registration_link: event.registration_link || '',
        image_url: event.image_url || '',
      });
      setDate(parseISO(event.date));
    } catch (error) {
      toast.error('Event not found');
      navigate('/events');
    } finally {
      setFetching(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date) {
      toast.error('Please select a date');
      return;
    }

    setLoading(true);
    try {
      const eventData = {
        ...formData,
        date: format(date, 'yyyy-MM-dd'),
        capacity: formData.capacity ? parseInt(formData.capacity) : null,
      };

      await axios.put(`${API}/events/${id}`, eventData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Event updated successfully!');
      navigate(`/events/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating event');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || fetching) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12">
      <div className="container-custom max-w-3xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-gold mb-8"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="mb-10">
          <p className="overline text-gold mb-4">Edit</p>
          <h1 className="text-3xl md:text-4xl">Update Event</h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title" className="text-sm uppercase tracking-wider">
              Event Title *
            </Label>
            <Input
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              className="rounded-sm border-border/50"
              data-testid="event-title-input"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-sm uppercase tracking-wider">
              Description *
            </Label>
            <Textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows={6}
              className="rounded-sm border-border/50 resize-none"
              data-testid="event-description-input"
            />
          </div>

          {/* Category and Date */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-sm uppercase tracking-wider">Category *</Label>
              <Select value={formData.category} onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}>
                <SelectTrigger className="rounded-sm border-border/50" data-testid="category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-sm uppercase tracking-wider">Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal rounded-sm border-border/50"
                    data-testid="date-picker-btn"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, 'PPP') : <span className="text-muted-foreground">Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Time */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="start_time" className="text-sm uppercase tracking-wider">
                Start Time *
              </Label>
              <Input
                id="start_time"
                name="start_time"
                type="time"
                value={formData.start_time}
                onChange={handleChange}
                required
                className="rounded-sm border-border/50"
                data-testid="start-time-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="end_time" className="text-sm uppercase tracking-wider">
                End Time *
              </Label>
              <Input
                id="end_time"
                name="end_time"
                type="time"
                value={formData.end_time}
                onChange={handleChange}
                required
                className="rounded-sm border-border/50"
                data-testid="end-time-input"
              />
            </div>
          </div>

          {/* Location */}
          <div className="space-y-2">
            <Label htmlFor="location" className="text-sm uppercase tracking-wider">
              Venue Name *
            </Label>
            <Input
              id="location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              required
              className="rounded-sm border-border/50"
              data-testid="location-input"
            />
          </div>

          {/* Address */}
          <div className="space-y-2">
            <Label htmlFor="address" className="text-sm uppercase tracking-wider">
              Full Address *
            </Label>
            <Input
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              required
              className="rounded-sm border-border/50"
              data-testid="address-input"
            />
          </div>

          {/* Optional Fields */}
          <div className="border-t border-border/50 pt-8">
            <p className="overline text-muted-foreground mb-6">Optional Details</p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="capacity" className="text-sm uppercase tracking-wider">
                  Capacity
                </Label>
                <Input
                  id="capacity"
                  name="capacity"
                  type="number"
                  value={formData.capacity}
                  onChange={handleChange}
                  min="1"
                  className="rounded-sm border-border/50"
                  data-testid="capacity-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="registration_link" className="text-sm uppercase tracking-wider">
                  External Registration Link
                </Label>
                <Input
                  id="registration_link"
                  name="registration_link"
                  type="url"
                  value={formData.registration_link}
                  onChange={handleChange}
                  className="rounded-sm border-border/50"
                  data-testid="registration-link-input"
                />
              </div>
            </div>

            <div className="space-y-2 mt-6">
              <Label htmlFor="image_url" className="text-sm uppercase tracking-wider">
                Event Image URL
              </Label>
              <Input
                id="image_url"
                name="image_url"
                type="url"
                value={formData.image_url}
                onChange={handleChange}
                className="rounded-sm border-border/50"
                data-testid="image-url-input"
              />
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-4 pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              className="flex-1 rounded-sm uppercase tracking-widest"
              data-testid="cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gold text-primary hover:bg-gold/90 rounded-sm uppercase tracking-widest"
              data-testid="update-event-btn"
            >
              {loading ? <span className="spinner mr-2" /> : null}
              Update Event
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditEvent;
